import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  GitBranch,
  Plus,
  Copy,
  Download,
  Upload,
  GitMerge,
  CheckCircle,
} from 'lucide-react';
import { useGit } from '@/hooks/useGit';
import { useGitStatus } from '@/hooks/useGitStatus';
import { useToast } from '@/hooks/useToast';
import { MergeDialog } from './MergeDialog';

interface RepositoryInfoProps {
  projectId: string;
  remoteUrl?: string | null;
}

export function RepositoryInfo({ projectId, remoteUrl }: RepositoryInfoProps) {
  const [editableRemoteUrl, setEditableRemoteUrl] = useState(remoteUrl || '');
  const [shakespeareUrl, setShakespeareUrl] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [isPulling, setIsPulling] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);

  const { git } = useGit();
  const { data: gitStatus, refetch: refetchGitStatus } = useGitStatus(projectId);
  const { toast } = useToast();
  const projectPath = `/projects/${projectId}`;

  useEffect(() => {
    setEditableRemoteUrl(remoteUrl || '');

    // Generate Shakespeare URL
    if (remoteUrl) {
      const encoded = encodeURIComponent(remoteUrl);
      setShakespeareUrl(`https://afterhours.local-shakespeare.dev/clone?url=${encoded}`);
    }
  }, [remoteUrl]);

  useEffect(() => {
    loadBranches();
  }, [projectId]);

  useEffect(() => {
    if (gitStatus?.currentBranch) {
      setSelectedBranch(gitStatus.currentBranch);
    }
  }, [gitStatus?.currentBranch]);

  const loadBranches = async () => {
    try {
      const branchList = await git.listBranches({ dir: projectPath });
      setBranches(branchList);
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  };

  const handleSaveRemoteUrl = async () => {
    try {
      await git.addRemote({
        dir: projectPath,
        remote: 'origin',
        url: editableRemoteUrl,
        force: true,
      });

      toast({
        title: 'Remote URL saved',
        description: 'Git remote URL has been updated',
      });

      refetchGitStatus();
    } catch (err) {
      toast({
        title: 'Failed to save remote URL',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: 'Copied to clipboard',
      description: 'URL copied successfully',
    });
  };

  const handleBranchChange = async (branchName: string) => {
    try {
      await git.checkout({
        dir: projectPath,
        ref: branchName,
      });

      setSelectedBranch(branchName);
      refetchGitStatus();

      toast({
        title: 'Branch switched',
        description: `Switched to branch "${branchName}"`,
      });
    } catch (err) {
      toast({
        title: 'Failed to switch branch',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleCreateBranch = async () => {
    if (!newBranchName.trim()) {
      toast({
        title: 'Branch name required',
        description: 'Please enter a branch name',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingBranch(true);
    try {
      await git.branch({
        dir: projectPath,
        ref: newBranchName.trim(),
      });

      await loadBranches();
      setNewBranchName('');

      toast({
        title: 'Branch created',
        description: `Created branch "${newBranchName.trim()}"`,
      });
    } catch (err) {
      toast({
        title: 'Failed to create branch',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingBranch(false);
    }
  };

  const handlePull = async () => {
    setIsPulling(true);
    try {
      await git.pull({
        dir: projectPath,
        ref: selectedBranch,
        singleBranch: true,
      });

      refetchGitStatus();

      toast({
        title: 'Pull successful',
        description: 'Latest changes pulled from remote',
      });
    } catch (err) {
      toast({
        title: 'Pull failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsPulling(false);
    }
  };

  const handlePush = async () => {
    setIsPushing(true);
    try {
      await git.push({
        dir: projectPath,
        ref: selectedBranch,
      });

      refetchGitStatus();

      toast({
        title: 'Push successful',
        description: 'Changes pushed to remote',
      });
    } catch (err) {
      toast({
        title: 'Push failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsPushing(false);
    }
  };

  const aheadBehind = gitStatus?.ahead || gitStatus?.behind;
  const hasUncommittedChanges = (gitStatus?.modified?.length || 0) > 0 ||
    (gitStatus?.staged?.length || 0) > 0 ||
    (gitStatus?.deleted?.length || 0) > 0 ||
    (gitStatus?.untracked?.length || 0) > 0;

  return (
    <div className="space-y-4">
      {/* Git URL */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Git URL</Label>
        <div className="flex gap-2">
          <Input
            value={editableRemoteUrl}
            onChange={(e) => setEditableRemoteUrl(e.target.value)}
            placeholder="nostr://npub.../repo-id"
            className="flex-1 font-mono text-xs h-9"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={handleCopyUrl.bind(null, editableRemoteUrl)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={handleSaveRemoteUrl}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Edit with Shakespeare URL */}
      {shakespeareUrl && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Edit with Shakespeare URL</Label>
          <div className="flex gap-2">
            <Input
              value={shakespeareUrl}
              readOnly
              className="flex-1 font-mono text-xs h-9 bg-muted/50"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handleCopyUrl.bind(null, shakespeareUrl)}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Current Branch */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Current Branch</Label>
        <div className="flex gap-2">
          <Select value={selectedBranch} onValueChange={handleBranchChange}>
            <SelectTrigger className="flex-1 h-9">
              <div className="flex items-center gap-2">
                <GitBranch className="h-3.5 w-3.5" />
                <SelectValue placeholder="Select branch" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => (
                <SelectItem key={branch} value={branch}>
                  {branch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => {
              const name = prompt('Enter new branch name:');
              if (name) {
                setNewBranchName(name);
                handleCreateBranch();
              }
            }}
            disabled={isCreatingBranch}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Sync Status */}
      {aheadBehind && (
        <div className="space-y-2.5">
          <Label className="text-xs text-muted-foreground">Sync Status</Label>
          <div className="flex items-center gap-2">
            {gitStatus.ahead ? (
              <Badge variant="secondary" className="gap-1.5 text-xs">
                <Upload className="h-3 w-3" />
                {gitStatus.ahead} commit{gitStatus.ahead !== 1 ? 's' : ''} ahead
              </Badge>
            ) : null}
            {gitStatus.behind ? (
              <Badge variant="secondary" className="gap-1.5 text-xs">
                <Download className="h-3 w-3" />
                {gitStatus.behind} commit{gitStatus.behind !== 1 ? 's' : ''} behind
              </Badge>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePull}
              disabled={isPulling || !gitStatus.behind}
              className="flex-1 h-9"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Pull
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePush}
              disabled={isPushing || !gitStatus.ahead}
              className="flex-1 h-9"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Push
            </Button>
          </div>
        </div>
      )}

      {/* Merge Branch */}
      <div className="pt-3 border-t">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsMergeDialogOpen(true)}
          className="h-9"
        >
          <GitMerge className="h-3.5 w-3.5 mr-1.5" />
          Merge Branch
        </Button>
      </div>

      {/* Patches */}
      <div className="pt-3 border-t space-y-2">
        <div>
          <h3 className="text-sm font-medium">Patches</h3>
          <p className="text-xs text-muted-foreground">
            Submit patches to the repository
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-9">
          <GitBranch className="h-3.5 w-3.5 mr-1.5" />
          Create Pull Request
        </Button>
      </div>

      {/* Working Directory */}
      <div className="pt-3 border-t space-y-2.5">
        <h3 className="text-sm font-medium">Working Directory</h3>
        {!hasUncommittedChanges ? (
          <div className="py-6 text-center space-y-1.5">
            <CheckCircle className="h-10 w-10 text-green-600 mx-auto" />
            <div>
              <p className="text-sm font-medium">Working directory is clean</p>
              <p className="text-xs text-muted-foreground">No uncommitted changes</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1 text-xs text-muted-foreground">
            {(gitStatus?.modified?.length || 0) > 0 && (
              <p>Modified: {gitStatus?.modified?.length} file{gitStatus?.modified?.length !== 1 ? 's' : ''}</p>
            )}
            {(gitStatus?.staged?.length || 0) > 0 && (
              <p>Staged: {gitStatus?.staged?.length} file{gitStatus?.staged?.length !== 1 ? 's' : ''}</p>
            )}
            {(gitStatus?.deleted?.length || 0) > 0 && (
              <p>Deleted: {gitStatus?.deleted?.length} file{gitStatus?.deleted?.length !== 1 ? 's' : ''}</p>
            )}
            {(gitStatus?.untracked?.length || 0) > 0 && (
              <p>Untracked: {gitStatus?.untracked?.length} file{gitStatus?.untracked?.length !== 1 ? 's' : ''}</p>
            )}
          </div>
        )}
      </div>

      <MergeDialog
        projectId={projectId}
        currentBranch={selectedBranch}
        open={isMergeDialogOpen}
        onOpenChange={setIsMergeDialogOpen}
        onMergeComplete={() => refetchGitStatus()}
      />
    </div>
  );
}
